// Shared helpers for the Overview / News / Competitors renderers (PDF + screen).

export const stripHtml = (s?: string | null): string =>
    (s ?? '')
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

export const splitBullets = (s: string): string[] => {
    const parts = s
        .split(/\s*[-·•]\s+/)
        .map((p) => p.trim())
        .filter(Boolean);
    return parts.length > 1 ? parts : [s];
};

export const truncateText = (s: string, max: number): string => {
    const clean = s.trim();
    if (clean.length <= max) return clean;
    const cut = clean.slice(0, max);
    const lastSpace = cut.lastIndexOf(' ');
    return (lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd() + '…';
};

// "Economic Times" → "ET", "VCCircle" → "VC", "Manufacturing Frontier" → "MF"
export const sourceInitials = (name: string): string => {
    const clean = name.trim();
    if (!clean) return '—';
    const words = clean.split(/\s+/).filter(Boolean);
    if (words.length === 1) {
        // Single word — take first two consonants/uppercase letters where possible.
        const w = words[0];
        const upper = w.match(/[A-Z]/g) ?? [];
        if (upper.length >= 2) return upper.slice(0, 2).join('');
        return w.slice(0, 2).toUpperCase();
    }
    return words
        .slice(0, 2)
        .map((w) => w[0]?.toUpperCase() ?? '')
        .join('');
};

export type RiskBand = 'LOW' | 'MEDIUM' | 'HIGH' | 'SEVERE';

// AICA score scale is 0–4. Cutoffs proportional: LOW ≥ 3, MEDIUM ≥ 2, HIGH ≥ 1, SEVERE below.
export const SCORE_MAX = 4;

export const scoreToBand = (score: number | undefined | null): RiskBand | null => {
    if (score == null || !Number.isFinite(score) || score <= 0) return null;
    if (score >= 3) return 'LOW';
    if (score >= 2) return 'MEDIUM';
    if (score >= 1) return 'HIGH';
    return 'SEVERE';
};

export const bandLabel = (band: RiskBand): string =>
    band === 'LOW' ? 'LOW' : band === 'MEDIUM' ? 'MEDIUM' : band === 'HIGH' ? 'HIGH' : 'SEVERE';

// Width fraction of the rg progress bar fill — AICA's 0–4 scale.
export const scoreBarFraction = (score: number | undefined | null): number => {
    if (score == null || !Number.isFinite(score)) return 0;
    return Math.max(0, Math.min(1, score / SCORE_MAX));
};

export const sentimentTone = (s: string | undefined | null): 'pos' | 'neg' | 'neutral' => {
    const v = (s ?? '').toLowerCase();
    if (v === 'positive') return 'pos';
    if (v === 'negative') return 'neg';
    return 'neutral';
};
